import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import {
  FaPlus,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaFilePdf,
  FaFileCsv,
  FaSearch,
  FaUsers,
  FaPhone,
  FaVenusMars,
  FaChalkboardTeacher,
  FaChevronCircleDown,
} from "react-icons/fa";
import { MdOpenInNew } from "react-icons/md";
import { toast } from "react-toastify";
import { API_URL } from "../config";
import DeleteDialog from "../components/dialog/delete";
import StudentModal from "../components/modal/studentsModal";
import { useNavigate } from "react-router-dom";
import ImagePreviewModal from "../components/modal/imagePreview";
import StudentLimitModal from "../components/modal/studentLimitModal";

/* ── Avatar palettes ───────────────────────── */
const PALETTES = [
  ["#0284c7", "#38bdf8"],
  ["#7c3aed", "#c4b5fd"],
  ["#b45309", "#fcd34d"],
  ["#047857", "#6ee7b7"],
  ["#be185d", "#f9a8d4"],
  ["#b91c1c", "#fca5a5"],
  ["#0e7490", "#67e8f9"],
  ["#4338ca", "#a5b4fc"],
];

/* ── Table header ──────────────────────────── */
const TableHeader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      py: 1,
      background:
        "linear-gradient(135deg, rgba(2,136,209,0.07) 0%, rgba(14,165,233,0.03) 100%)",
      borderBottom: "1px solid rgba(2,136,209,0.1)",
    }}
  >
    <Box sx={{ flex: "0 0 40px" }} />
    {[
      { label: "Student", flex: "2 1 0" },
      { label: "Email", flex: "2 1 0" },
      { label: "Contact", flex: "2 1 0" },
      { label: "Gender", flex: "0 0 88px" },
      { label: "Code", flex: "0 0 100px" },
    ].map(({ label, flex }) => (
      <Typography
        key={label}
        sx={{
          flex,
          fontSize: "0.63rem",
          fontWeight: 800,
          color: "#94a3b8",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    ))}
    <Box sx={{ flex: "0 0 148px" }} />
  </Box>
);

/* ── Single student row ─────────────────────── */
const StudentRow = ({
  student,
  index,
  onEdit,
  onDelete,
  expanded,
  onToggleExpand,
  subjects,
  isLast,
  goToActivityPage,
  onAvatarClick,
}) => {
  const initials =
    student.name
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "S";

  const [c1, c2] = PALETTES[index % PALETTES.length];

  const genderStyle =
    student.gender === "Male"
      ? {
          bg: "rgba(2,136,209,0.08)",
          color: "#0288D1",
          border: "rgba(2,136,209,0.18)",
        }
      : student.gender === "Female"
      ? {
          bg: "rgba(219,39,119,0.08)",
          color: "#db2777",
          border: "rgba(219,39,119,0.18)",
        }
      : {
          bg: "rgba(148,163,184,0.07)",
          color: "#94a3b8",
          border: "rgba(148,163,184,0.18)",
        };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 0.9,
          minHeight: 52,
          borderBottom:
            isLast && !expanded ? "none" : "1px solid rgba(226,232,240,0.6)",
          background: "#fff",
          transition: "background 0.14s ease",
          "&:hover": {
            background:
              "linear-gradient(90deg, rgba(2,136,209,0.03) 0%, rgba(255,255,255,0) 100%)",
          },
          "&:hover .row-actions": {
            opacity: 1,
            transform: "translateX(0)",
          },
        }}
      >
        {/* Avatar */}
        <Box sx={{ flex: "0 0 40px" }}>
          <Avatar
            src={student.passport}
            sx={{
              width: 40,
              height: 40,
              fontSize: "0.68rem",
              fontWeight: 800,
              background: student.passport
                ? "transparent"
                : `linear-gradient(135deg, ${c1}, ${c2})`,
              boxShadow: `0 2px 6px ${c1}50`,
              border: "1.5px solid #fff",
              cursor: "pointer",
              marginRight: "15px",
              transition: "transform .18s ease, box-shadow .18s ease",
              "&:hover": {
                transform: "scale(1.08)",
                boxShadow: `0 4px 14px ${c1}70`,
              },
            }}
            onClick={() =>
              onAvatarClick({
                src: student.passport,
                name: student.name,
                subtitle: student.student_code,
                fallback: initials,
                avatarColor: `linear-gradient(135deg, ${c1}, ${c2})`,
              })
            }
          >
            {!student.passport && initials}
          </Avatar>
        </Box>

        {/* Name */}
        <Box sx={{ flex: "2 1 0", minWidth: 0, pr: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.84rem",
              color: "#0f172a",
              textTransform: "capitalize",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}
          >
            {student.name}
          </Typography>
          {student.date_of_birth && (
            <Typography
              sx={{ fontSize: "0.67rem", color: "#94a3b8", lineHeight: 1.3 }}
            >
              DOB:{" "}
              {new Date(student.date_of_birth).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </Typography>
          )}
        </Box>

        {/* Email */}
        <Box sx={{ flex: "2 1 0", minWidth: 0, pr: 2 }}>
          {student.email ? (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#475569",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.4,
              }}
            >
              {student.email}
            </Typography>
          ) : (
            <Typography
              sx={{ fontSize: "0.72rem", color: "#e2e8f0", lineHeight: 1.4 }}
            >
              No email
            </Typography>
          )}
        </Box>

        {/* Phone */}
        <Box sx={{ flex: "2 1 0", minWidth: 0, pr: 2 }}>
          {student.phone ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FaPhone size={9} color="#94a3b8" />
              <Typography
                sx={{ fontSize: "0.7rem", color: "#64748b", lineHeight: 1.4 }}
              >
                {student.phone}
              </Typography>
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "0.7rem", color: "#e2e8f0", lineHeight: 1.4 }}
            >
              No phone
            </Typography>
          )}
        </Box>

        {/* Gender */}
        <Box sx={{ flex: "0 0 88px" }}>
          {student.gender ? (
            <Chip
              icon={<FaVenusMars size={8} />}
              label={student.gender}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.63rem",
                fontWeight: 700,
                background: genderStyle.bg,
                color: genderStyle.color,
                border: `1px solid ${genderStyle.border}`,
                "& .MuiChip-label": { px: 0.7 },
                "& .MuiChip-icon": { color: genderStyle.color, ml: 0.5 },
              }}
            />
          ) : (
            <Typography sx={{ fontSize: "0.69rem", color: "#e2e8f0" }}>
              —
            </Typography>
          )}
        </Box>

        {/* Student Code */}
        <Box sx={{ flex: "0 0 100px" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.2,
              borderRadius: 1.2,
              background: "rgba(2,136,209,0.07)",
              border: "1px solid rgba(2,136,209,0.15)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.67rem",
                fontWeight: 700,
                color: "#0288D1",
                letterSpacing: "0.05em",
                fontFamily: "'JetBrains Mono','Fira Code',monospace",
              }}
            >
              {student.student_code || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box
          className="row-actions"
          sx={{
            flex: "0 0 148px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.6,
            opacity: 0.45,
            transform: "translateX(4px)",
            transition: "all 0.16s ease",
          }}
        >
          <Tooltip
            title={expanded ? "Hide subjects" : "View subjects"}
            placement="top"
            arrow
          >
            <IconButton
              size="small"
              onClick={() => onToggleExpand(student.id)}
              sx={{
                width: 26,
                height: 26,
                borderRadius: 1.4,
                border: `1px solid ${
                  expanded ? "rgba(2,136,209,0.3)" : "rgba(100,116,139,0.2)"
                }`,
                color: expanded ? "#0288D1" : "#64748b",
                background: expanded ? "rgba(2,136,209,0.06)" : "transparent",
                "&:hover": {
                  color: "#0288D1",
                  borderColor: "rgba(2,136,209,0.35)",
                  background: "rgba(2,136,209,0.06)",
                },
              }}
            >
              <FaChevronCircleDown size={10} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Remove student" placement="top" arrow>
            <IconButton
              size="small"
              onClick={() => onDelete(student.id)}
              sx={{
                width: 26,
                height: 26,
                borderRadius: 1.4,
                border: "1px solid rgba(244,63,94,0.15)",
                color: "#f43f5e",
                "&:hover": {
                  background: "rgba(244,63,94,0.06)",
                  borderColor: "rgba(244,63,94,0.3)",
                },
              }}
            >
              <FaTrash size={9} />
            </IconButton>
          </Tooltip>

          <Tooltip title="View & edit" placement="top" arrow>
            <Button
              size="small"
              variant="contained"
              endIcon={<MdOpenInNew size={9} />}
              onClick={() => onEdit(student)}
              sx={{
                borderRadius: 1.4,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.69rem",
                px: 1.1,
                py: 0,
                height: 26,
                minWidth: 0,
                background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                boxShadow: "0 2px 6px rgba(2,136,209,0.22)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0277bd, #0288D1)",
                  boxShadow: "0 3px 10px rgba(2,136,209,0.35)",
                },
              }}
            >
              Edit
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Subjects expand */}
      {expanded && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: isLast ? "none" : "1px solid rgba(226,232,240,0.6)",
            background:
              "linear-gradient(135deg, rgba(2,136,209,0.02), rgba(255,255,255,0))",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <FaChalkboardTeacher size={11} color="#94a3b8" />
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Subjects
            </Typography>
          </Stack>
          {subjects.length > 0 ? (
            <Stack direction="row" spacing={0.8} flexWrap="wrap" gap={0.8}>
              {subjects.map((subj) => (
                <Box
                  key={subj.id}
                  sx={{
                    px: 1.2,
                    py: 0.3,
                    borderRadius: 1.5,
                    background: subj.taken_exam
                      ? "rgba(16,185,129,0.08)"
                      : "#f1f5f9",
                    color: subj.taken_exam ? "#059669" : "#64748b",
                    border: `1px solid ${
                      subj.taken_exam ? "rgba(16,185,129,0.25)" : "#e2e8f0"
                    }`,
                    cursor: "pointer",
                    "&:hover": {
                      background: subj.taken_exam
                        ? "rgba(16,185,129,0.15)"
                        : "rgba(2,136,209,0.08)",
                      borderColor: subj.taken_exam ? "#10B981" : "#0288D1",
                      color: subj.taken_exam ? "#059669" : "#0288D1",
                    },
                  }}
                  onClick={() => goToActivityPage(subj.id)}
                >
                  <Typography
                    sx={{
                      fontSize: "0.71rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {subj.name}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#94a3b8",
                fontStyle: "italic",
              }}
            >
              No subjects assigned
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

/* ── Skeleton row ──────────────────────────── */
const SkeletonRow = ({ isLast }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      py: 0.9,
      minHeight: 52,
      borderBottom: isLast ? "none" : "1px solid rgba(226,232,240,0.6)",
      background: "#fff",
    }}
  >
    <Box sx={{ flex: "0 0 40px" }}>
      <Skeleton variant="circular" width={32} height={32} />
    </Box>
    <Box sx={{ flex: "2 1 0", pr: 2 }}>
      <Skeleton width="46%" height={13} />
      <Skeleton width="28%" height={11} sx={{ mt: 0.3 }} />
    </Box>
    <Box sx={{ flex: "2 1 0", pr: 2 }}>
      <Skeleton width="62%" height={13} />
      <Skeleton width="40%" height={11} sx={{ mt: 0.3 }} />
    </Box>
    <Box sx={{ flex: "0 0 88px" }}>
      <Skeleton width={50} height={20} sx={{ borderRadius: 99 }} />
    </Box>
    <Box sx={{ flex: "0 0 100px" }}>
      <Skeleton width={42} height={20} sx={{ borderRadius: 1.2 }} />
    </Box>
    <Box
      sx={{
        flex: "0 0 148px",
        display: "flex",
        justifyContent: "flex-end",
        gap: 0.6,
      }}
    >
      <Skeleton width={26} height={26} sx={{ borderRadius: 1.4 }} />
      <Skeleton width={26} height={26} sx={{ borderRadius: 1.4 }} />
      <Skeleton width={26} height={26} sx={{ borderRadius: 1.4 }} />
      <Skeleton width={46} height={26} sx={{ borderRadius: 1.4 }} />
    </Box>
  </Box>
);

/* ════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════ */
const InstStudentPage = ({
  classId,
  token,
  students,
  setStudents,
  fetchData,
  totalStudents,
  setTotalStudents,
  handleDownloadStudentPDF,
  handleDownloadStudentCSV,
  loading = false,
}) => {
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudents, setShowStudents] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [openLimitModal, setOpenLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");
  const [search, setSearch] = useState("");

  const [access, setAccess] = useState(true);

  const isDisabled = selectedStudent && !access;

  // Image preview state — holds { src, name, subtitle, fallback, avatarColor } or null
  const [previewData, setPreviewData] = useState(null);

  const navigate = useNavigate();

  const filtered = useMemo(() => {
    if (!search.trim()) return students;
    const q = search.toLowerCase();
    return students.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.student_code?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q)
    );
  }, [students, search]);

  const expandedSubjects = useMemo(
    () => students.find((s) => s.id === expandedId)?.subjects || [],
    [expandedId, students]
  );

  useEffect(() => {
    if (selectedStudent) {
      console.log("Selected Student:", selectedStudent);
      console.log("Access Value:", selectedStudent.access);

      setAccess(selectedStudent.access ?? true);
    }
  }, [selectedStudent]);

  useEffect(() => {
    console.log("Students:", students);
  }, [students]);

  const handleStudentSubmit = async (formData) => {
    setLoadingAction(true);
    const isEdit = !!selectedStudent;
    const url = isEdit
      ? `${API_URL}/instclass/${classId}/students/${selectedStudent.id}/`
      : `${API_URL}/instclass/${classId}/students/`;

    formData.set("Access", access ? "true" : "false");
    console.log("Access state:", access);
    console.log("FormData Access:", formData.get("Access"));
    try {
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token.access}` },
        body: formData,
      });
      if (res.ok) {
        toast.success(
          `Student ${isEdit ? "updated" : "created"} successfully!`
        );
        fetchData();
        setOpenModal(false);
      } else {
        const err = await res.json();
        const errorMessage =
          err.error || err.detail || err.message || "An error occurred";
        if (errorMessage.toLowerCase().includes("student limit")) {
          setOpenModal(false);
          setLimitMessage(errorMessage);
          setOpenLimitModal(true);

          return;
        }

        toast.error(errorMessage);
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async () => {
    setLoadingAction(true);
    try {
      const res = await fetch(
        `${API_URL}/instclass/${classId}/students/${selectedStudentId}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token.access}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            teacher_code: localStorage.getItem("teacher_code"),
          }),
        }
      );
      if (res.ok) {
        setStudents((prev) => {
          const updatedStudents = prev.filter(
            (s) => s.id !== selectedStudentId
          );

          setTotalStudents(updatedStudents.length);

          return updatedStudents;
        });
        toast.success("Student removed from roster ✅");
        setOpenConfirm(false);
      } else {
        const err = await res.json().catch(() => null);
        if (res.status === 403) {
          toast.error(
            err?.error || "You are not allowed to delete this student ❌"
          );
        } else {
          toast.error("Delete failed ❌");
        }
      }
    } catch {
      toast.error("Network error ❌");
    } finally {
      setLoadingAction(false);
    }
  };

  const goToActivityPage = (subjectId) => {
    navigate(`/activities?subject_id=${subjectId}`, { state: { students } });
  };

  console.log(students);
  console.log(access);

  return (
    <Box sx={{ pb: 4 }}>
      {/* ── Top section ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: "#fff",
          border: "1px solid rgba(2,136,209,0.15)",
          boxShadow: "0 2px 16px rgba(2,136,209,0.07)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #e2e8f0",
            background:
              "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(2,136,209,0.28)",
                }}
              >
                <FaUsers size={14} color="#fff" />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: "#0c1a2e",
                    fontSize: "0.92rem",
                    lineHeight: 1.2,
                  }}
                >
                  Student Roster
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#94a3b8", fontWeight: 500 }}
                >
                  {totalStudents ?? students.length} student
                  {(totalStudents ?? students.length) !== 1 ? "s" : ""} enrolled
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <TextField
                placeholder="Search students…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch size={12} color="#94a3b8" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    color: "#0c1a2e",
                    borderRadius: 2,
                    background: "#fff",
                    fontSize: "0.84rem",
                    "& fieldset": { borderColor: "rgba(2,136,209,0.2)" },
                    "&:hover fieldset": { borderColor: "#0288D1" },
                    "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                  },
                  "& input::placeholder": {
                    color: "#94a3b8",
                    fontSize: "0.84rem",
                  },
                }}
              />

              <Tooltip title={showStudents ? "Hide list" : "Show list"} arrow>
                <IconButton
                  size="small"
                  onClick={() => setShowStudents((v) => !v)}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    border: "1px solid rgba(2,136,209,0.2)",
                    color: "#64748b",
                    "&:hover": {
                      borderColor: "#0288D1",
                      color: "#0288D1",
                      background: "rgba(2,136,209,0.04)",
                    },
                  }}
                >
                  {showStudents ? (
                    <FaEyeSlash size={13} />
                  ) : (
                    <FaEye size={13} />
                  )}
                </IconButton>
              </Tooltip>

              <Button
                variant="contained"
                size="small"
                startIcon={<FaPlus size={10} />}
                onClick={() => {
                  setSelectedStudent(null);
                  setOpenModal(true);
                }}
                sx={{
                  borderRadius: 99,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 2,
                  py: 0.85,
                  background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                  boxShadow: "0 4px 12px rgba(2,136,209,0.25)",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    background: "linear-gradient(135deg, #0277bd, #0288D1)",
                    boxShadow: "0 6px 16px rgba(2,136,209,0.35)",
                  },
                }}
              >
                New Student
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Table body */}
        {showStudents && (
          <Box>
            {loading ? (
              <>
                <TableHeader />
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonRow key={i} isLast={i === 4} />
                ))}
              </>
            ) : filtered.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 7,
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2.5,
                    background: "rgba(2,136,209,0.06)",
                    border: "1px dashed rgba(2,136,209,0.22)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaUsers size={18} color="#94a3b8" />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.88rem",
                    fontWeight: 700,
                    color: "#64748b",
                  }}
                >
                  No students found
                </Typography>
                <Typography sx={{ fontSize: "0.74rem", color: "#94a3b8" }}>
                  {search
                    ? "Try a different search term"
                    : "Add a student to get started"}
                </Typography>
              </Box>
            ) : (
              <>
                <TableHeader />
                {filtered.map((student, i) => (
                  <StudentRow
                    key={student.id}
                    student={student}
                    index={i}
                    onEdit={(s) => {
                      console.log("EDIT STUDENT:", s);
                      setSelectedStudent(s);
                      setOpenModal(true);
                    }}
                    onDelete={(id) => {
                      setSelectedStudentId(id);
                      setOpenConfirm(true);
                    }}
                    expanded={expandedId === student.id}
                    onToggleExpand={(id) =>
                      setExpandedId((prev) => (prev === id ? null : id))
                    }
                    subjects={expandedId === student.id ? expandedSubjects : []}
                    isLast={i === filtered.length - 1}
                    goToActivityPage={goToActivityPage}
                    onAvatarClick={setPreviewData}
                  />
                ))}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Export row */}
      <Stack direction="row" spacing={1.5} mt={2}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<FaFilePdf size={12} />}
          onClick={() => handleDownloadStudentPDF(classId)}
          sx={{
            py: 1.1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.8rem",
            borderColor: "rgba(244,63,94,0.25)",
            color: "#f43f5e",
            "&:hover": {
              borderColor: "#f43f5e",
              background: "rgba(244,63,94,0.04)",
            },
          }}
        >
          Export PDF
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<FaFileCsv size={12} />}
          onClick={() => handleDownloadStudentCSV(classId)}
          sx={{
            py: 1.1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.8rem",
            borderColor: "rgba(5,150,105,0.25)",
            color: "#059669",
            "&:hover": {
              borderColor: "#059669",
              background: "rgba(5,150,105,0.04)",
            },
          }}
        >
          Export CSV
        </Button>
      </Stack>

      {/* Modals */}
      <StudentModal
        open={openModal}
        initialData={selectedStudent}
        onClose={() => setOpenModal(false)}
        onSubmit={handleStudentSubmit}
        loading={loadingAction}
        access={access}
        setAccess={setAccess}
        isDisabled={isDisabled}
      />
      <StudentLimitModal
        open={openLimitModal}
        onClose={() => setOpenLimitModal(false)}
        message={limitMessage}
        onUpgrade={() => navigate("/inst-sub-plans")}
      />

      <DeleteDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleDelete}
        title="Student"
        loading={loadingAction}
      />

      {/* ── Image Preview Modal ── */}
      <ImagePreviewModal
        open={!!previewData}
        onClose={() => setPreviewData(null)}
        src={previewData?.src}
        name={previewData?.name}
        subtitle={previewData?.subtitle}
        fallback={previewData?.fallback}
        avatarColor={previewData?.avatarColor}
      />
    </Box>
  );
};

export default InstStudentPage;
